import React from 'react';
import { Link } from 'react-router-dom';

import { StarFilled } from '@ant-design/icons';
import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import { VerticalDivider } from 'components/lib/VerticalDivider';
import { AGGREGATION_TYPE, AGGREGATION_TYPE_LABELS } from 'components/View/constants';
import { useAppSelector } from 'store/hook';
import { formatPrice, formatScore } from 'utils/helpers/numbers';

import { MarketPositioningPoint } from './types';
import { ACTOR_TYPE } from '../../../constants';
import { WIDGET_TYPES } from '../constants';
import ValueDifference from '../ValueDifference';

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const EntryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
`;

const EntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const EntryStats = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--text-primary);
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Rate = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: var(--text-primary);
`;

const ScoreStats = styled.div`
  display: flex;
  flex-flow: row;
  align-items: baseline;
  gap: 4px;
  font-family: var(--font-regular);
  font-size: 12px;
  color: var(--text-primary);

  .score-value {
    font-size: 16px;
  }
`;

const Dot = styled.span<{ color?: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 2px;
  background-color: ${(props) => props.color};
`;

const Row = styled.div`
  display: flex;
  flex-flow: row;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

const EntryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  text-decoration: none;
`;

type Props = {
  community?: MarketPositioningPoint;
  valueDisplay?: number;
  roomTypesPriceDisplay?: number;
  useScoreFormat?: boolean;
};

const MarketPositioningCommunityEntry = ({ community, valueDisplay, roomTypesPriceDisplay, useScoreFormat }: Props) => {
  const aggregationLabel = (
    AGGREGATION_TYPE_LABELS[roomTypesPriceDisplay ?? AGGREGATION_TYPE.AVG] ??
    AGGREGATION_TYPE_LABELS[AGGREGATION_TYPE.AVG]
  ).toLowerCase();
  const { selectedCommunity } = useAppSelector((state) => state.global);
  if (!community) {
    return null;
  }

  const communityType = selectedCommunity?.id === community.community_id ? ACTOR_TYPE.COMMUNITY : ACTOR_TYPE.COMPETITOR;

  return (
    <Container>
      <Dot color={community.community_color} />
      <Row>
        <EntryCard>
          <EntryHeader>{community.community_name}</EntryHeader>
          {useScoreFormat ? (
            <ScoreStats>
              <span className="score-value">{Math.round(community.quality_score)}</span>
              <span>
                /100 &middot; {formatPrice(Math.round(community.market_rate), '$', 0, 0)} {aggregationLabel}
              </span>
            </ScoreStats>
          ) : (
            <EntryStats>
              <Rating>
                <StarFilled style={{ color: '#c6cad6', fontSize: 12, marginTop: '-3px' }} />
                {formatScore(community.quality_score)}
                <ValueDifference
                  difference={community.qualityDifference}
                  widgetType={WIDGET_TYPES.RATING_GRAPH}
                  valueDisplay={valueDisplay}
                />
              </Rating>
              <VerticalDivider />
              <Rate>
                {formatPrice(Math.round(community.market_rate), '$', 0, 0)} {aggregationLabel}
                <ValueDifference
                  difference={community.rateDifference}
                  widgetType={WIDGET_TYPES.MARKET_RATE_GRAPH}
                  valueDisplay={valueDisplay}
                />
              </Rate>
            </EntryStats>
          )}
        </EntryCard>
        {community.community_id && (
          <EntryLink
            to={`/${communityType}/${community.community_id}/info`}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${community.community_name} details`}
          >
            <ChevronRight fill="var(--text-primary)" width={16} height={16} />
          </EntryLink>
        )}
      </Row>
    </Container>
  );
};

export default MarketPositioningCommunityEntry;
